import { useSetReturnMode } from "@babackapp/return-widget-components/hooks/use-set-return-mode";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useLayoutEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { resolveDefaultReturnMode } from "../utils/return-modes";
import { useAvailableReturnModes } from "./use-available-return-modes";
import { useWidgetParams } from "./use-widget-params";
import type { ReturnMode } from "@babackapp/return-widget-components/types";
import {
  firstPickupDate,
  type PickupRule,
} from "@babackapp/return-widget-components/lib/pickup.lib";

const returnModeSchema = z
  .object({
    returnMode: z.string(),
    pickupDate: z.string().optional(),
    storeId: z.string().optional(),
    isPickup: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.returnMode === "carrier" && data.isPickup && !data.pickupDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["pickupDate"],
        message: "required",
      });
    }
    if (data.returnMode === "store" && !data.storeId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["storeId"],
        message: "required",
      });
    }
  });

type ReturnModeFormValues = {
  returnMode: ReturnMode;
  pickupDate?: string;
  storeId?: string;
  isPickup?: boolean;
};

export function useReturnModeForm() {
  const navigate = useNavigate();
  const { orderId } = useWidgetParams();
  const { data: order } = usePublicOrder(orderId);
  const setReturnMode = useSetReturnMode(orderId);
  const {
    availableReturnModes,
    selectableStores,
    hasStoreReturns,
    storeReturnState,
    isPickup,
    userPortalTexts,
    canSkip,
  } = useAvailableReturnModes();

  const pickupRule = (order?.pickupRule ?? null) as PickupRule | null;
  const defaultPickupDate = firstPickupDate(pickupRule);

  // Only a refusal steers the default away from the store: `defaultValues`
  // are read once, so skipping a store whose answer is merely pending would
  // keep the fallback after the answer allows it. Submitting a store while
  // its answer is pending is blocked instead (`isSubmitBlocked`).
  const firstAvailableMode = resolveDefaultReturnMode(
    availableReturnModes,
    storeReturnState.isRefused,
  );
  const form = useForm<ReturnModeFormValues>({
    resolver: zodResolver(returnModeSchema),
    defaultValues: {
      returnMode: firstAvailableMode,
      pickupDate: isPickup ? defaultPickupDate : undefined,
      storeId: undefined,
      isPickup,
    },
  });

  // The selected mode always belongs to the offered ones: a block that leaves
  // the screen once the order has loaded must not stay selected (BBS-385),
  // nor a store refused once the basket is evaluated (BBS-418). A pending
  // answer only blocks picking the store, it never takes it back.
  const selectedMode = form.watch("returnMode");
  const isSelectedModeOffered =
    availableReturnModes.includes(selectedMode) &&
    !(selectedMode === "store" && storeReturnState.isRefused);
  useEffect(() => {
    if (!isSelectedModeOffered) form.setValue("returnMode", firstAvailableMode);
  }, [form, isSelectedModeOffered, firstAvailableMode]);

  const isSubmitBlocked =
    selectedMode === "store" && storeReturnState.isPending;

  // A lone carrier drop-off leaves nothing to choose: record it and move on.
  // `setReturnMode` is left out of the deps on purpose: it changes on every
  // render, and the skip must only follow `canSkip`.
  useLayoutEffect(() => {
    if (!canSkip) return;
    setReturnMode("carrier", null, null);
    navigate("../address", { replace: true });
  }, [canSkip, navigate]);

  const onSubmit = form.handleSubmit(async (values) => {
    if (values.returnMode === "store" && storeReturnState.isPending) return;
    setReturnMode(
      values.returnMode,
      values.returnMode === "store" ? (values.storeId ?? null) : null,
      values.returnMode === "carrier" ? (values.pickupDate ?? null) : null,
    );
    navigate("../address");
  });

  return {
    form,
    onSubmit,
    availableReturnModes,
    selectableStores,
    hasStoreReturns,
    storeReturnState,
    isSubmitBlocked,
    isPickup,
    pickupRule,
    userPortalTexts,
    canSkip,
  };
}
